import { DefaultTheme, css } from "styled-components";

import { isNullOrWhitespace } from "@app/core/utilities/string";

export type ThemeProps = {
    theme: DefaultTheme;
};

function toCssRadiusVariable(radiuses: Radius[]) {
    return radiuses.map(radius => `--radius-${radius.radiusKey}: ${radius.value}`).join(";");
}

export function toCssColorVariable(colors: Color[]) {
    return colors.map(color => `--color-${color.colorKey}: ${color.value}`).join(";");
}

export const loadCssStyleBlock = (scope: string) => (props: ThemeProps) => {
    return (
        props.theme.tokens.customStyles.find((style: { customStyleKey: string }) => style.customStyleKey === scope)
            ?.value ?? ""
    );
};

function toCssShadowVariable(shadows: Shadow[]) {
    return shadows.map(shadow => `--shadow-${shadow.shadowKey}: ${shadow.value}`).join(";");
}

function toCssFontVariable(fonts: Font[]) {
    return fonts
        .map(font => {
            let css = "";
            if (font.family) css = `${css}; --font-family-${font.fontKey}: ${font.family};`;
            if (font.letterSpacing) css = `${css}; --font-letterSpacing-${font.fontKey}: ${font.letterSpacing}`;
            if (font.lineHeight) css = `${css}; --font-lineHeight-${font.fontKey}: ${font.lineHeight};`;
            if (font.size)
                css = `${css}; --font-size-${font.fontKey}: ${font.size}${isNullOrWhitespace(font.sizeUnit) ? "" : font.sizeUnit};`;
            if (font.textTransform) css = `${css}; --font-textTransform-${font.fontKey}: ${font.textTransform};`;
            if (font.variant) css = `${css}; --font-variant-${font.fontKey}: ${font.variant};`;
            if (font.weight) css = `${css}; --font-weight-${font.fontKey}: ${font.weight};`;

            return css;
        })
        .join(";");
}

export const tokens = css`
    :root {
        ${() => loadCssStyleBlock("common")};
        ${({ theme }) => toCssColorVariable(theme.tokens.colors)};
        ${({ theme }) => toCssRadiusVariable(theme.tokens.radius)};
        ${({ theme }) => toCssShadowVariable(theme.tokens.shadows)};
        ${({ theme }) => toCssFontVariable(theme.tokens.fonts)};
    }
`;

// Reflects chart colors from SCSS definitions in dit-layout/color-mapping.scss
const CHART_COLOR_MAPPINGS: Record<string, string[]> = {
    "chart-plotoption-pie": [
        "chart-plotoption-pie-1",
        "chart-plotoption-pie-2",
        "chart-plotoption-pie-3",
        "chart-plotoption-pie-4",
        "chart-plotoption-pie-5",
        "chart-plotoption-pie-6",
    ],
    "chart-plotoption-bar": [
        "chart-plotoption-bar-1",
        "chart-plotoption-bar-2",
        "chart-plotoption-bar-3",
        "chart-plotoption-bar-4",
        "chart-plotoption-bar-5",
        "chart-plotoption-bar-6",
    ],
};

export function getChartColors(theme: DefaultTheme, colorKeyPartial: string) {
    // Check for mapping first
    const mappedChartColors = CHART_COLOR_MAPPINGS[colorKeyPartial];
    if (mappedChartColors) {
        return mappedChartColors.map(key => `var(--dit-layout-color-${key}--map)`);
    }

    // Fallback to old tokens filtering
    return theme.tokens.colors
        .filter(color => color.colorKey.includes(colorKeyPartial))
        .map(color => `var(--color-${color.colorKey})`);
}

export function getEmptyChartColors() {
    return [`var(--dit-layout-color-card-body-background--map)`];
}

export function getEmptyChartFillColor() {
    return `var(--dit-brand-color-neutral-15--map)`;
}
